/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { FormattedMessage } from 'js/lib/coursera.react-intl';

import type { Theme } from '@coursera/cds-core';
import { ExternalLinkIcon } from '@coursera/cds-icons';

import PlagiarismDetectionResultLayout, {
  RESULT_TYPES,
} from 'bundles/academic-integrity/components/plagiarism-detection/PlagiarismDetectionResultLayout';
import PlagiarismReportLink from 'bundles/academic-integrity/components/plagiarism-detection/PlagiarismReportLink';
import type {
  SimilarityChecksResult,
  SimilarityChecksStatus,
} from 'bundles/academic-integrity/components/plagiarism-detection/SimilarityChecks';
import { SimilarityChecksStatuses } from 'bundles/academic-integrity/components/plagiarism-detection/SimilarityChecks';
import { getErrorMessagesMap } from 'bundles/academic-integrity/components/plagiarism-detection/constants/ErrorMessages';
import LearnerHelpLink from 'bundles/common/components/LearnerHelpLink';

import _t from 'i18n!nls/academic-integrity';

const styles = {
  link: (theme: Theme) =>
    css({
      marginTop: 'var(--cds-spacing-100)',
      display: 'flex',
    }),
  learnerHelpCenterLinkContainer: (theme: Theme) =>
    css({
      marginLeft: 'var(--cds-spacing-300)',
    }),
  learnerHelpCenterLink: (theme: Theme) =>
    css({
      marginRight: 'var(--cds-spacing-50)',
    }),
  externalLinkIcon: (theme: Theme) =>
    css({
      color: 'var(--cds-color-blue-700)',
      verticalAlign: 'text-bottom',
      marginInlineStart: 'var(--cds-spacing-50)',
    }),
};

type Props = {
  result?: SimilarityChecksResult;
  refetch: () => Promise<{}>;
  status: SimilarityChecksStatus;
};

const PlagiarismDetectionResult: React.FC<Props> = (props) => {
  const { result, refetch, status } = props;
  if (status === SimilarityChecksStatuses.IN_PROGRESS) {
    return null;
  }

  const failureResult = result?.orgCourseraSimilaritycheckSimilarityCheckFailure;
  const successResult = result?.orgCourseraSimilaritycheckSimilarityCheckSuccess;

  if (failureResult) {
    if (failureResult.errorMsg === 'PROCESSING_ERROR') {
      return (
        <div>
          <PlagiarismDetectionResultLayout type={RESULT_TYPES.RETRY} title={_t('Check again for plagiarism')} />
        </div>
      );
    }
    const errorMessagesMap = getErrorMessagesMap();
    return (
      <div>
        <PlagiarismDetectionResultLayout type={RESULT_TYPES.ERROR} title={_t('Edit your response')}>
          {errorMessagesMap[failureResult.errorMsg]}
        </PlagiarismDetectionResultLayout>
      </div>
    );
  } else if (!successResult) {
    return null;
  }

  const { score, url } = successResult;

  if (score === 0) {
    return (
      <div>
        <PlagiarismDetectionResultLayout type={RESULT_TYPES.NOT_DETECTED} title={_t('Plagiarism not detected')} />
      </div>
    );
  }

  return (
    <div>
      <PlagiarismDetectionResultLayout type={RESULT_TYPES.DETECTED} title={_t('Plagiarism detected')}>
        <div>
          <FormattedMessage
            message={_t(
              "This response has a {percentScore} similarity score. The text matches sources found online or other learner's submissions."
            )}
            percentScore={<strong>{score + '%'}</strong>}
          />
        </div>
        <div css={styles.link}>
          <PlagiarismReportLink refetch={refetch} link={url} />
          <div css={styles.learnerHelpCenterLinkContainer}>
            <LearnerHelpLink
              css={styles.learnerHelpCenterLink}
              articleId="360004031371"
              linkText={_t('Learner Help Center')}
            />
            <ExternalLinkIcon size="medium" css={styles.externalLinkIcon} />
          </div>
        </div>
      </PlagiarismDetectionResultLayout>
    </div>
  );
};

export default PlagiarismDetectionResult;
